/**
 * View as member — the tab's start-up and plumbing (state: services/viewAsState.ts,
 * server: Backend/admin_view_as.py).
 *
 * bootViewAs() runs before the app renders. On /#view_as=<code> it trades the
 * one-time code for the token (the code is gone from the address bar first,
 * and a fragment never reaches a server log). In a view-as tab it then:
 * - stamps X-Utech-View-As on every request to our API, raw fetch() calls
 *   included, so the server can pin each one to the member and keep it
 *   read-only — not only the calls that send a bearer token;
 * - drops the analytics beacon (navigator.sendBeacon), which cannot carry a
 *   header; the click tracker and PostHog check isViewAsTab() themselves.
 */
import { apiService } from '../services/apiService';
import {
  VIEW_AS_HEADER, isViewAsTab, liveViewAs, markViewAsEnded, setViewAs, type ViewAs,
} from '../services/viewAsState';

let installed = false;

function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const base = apiService.getApiBaseUrl();
  const realFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const v = liveViewAs();
    if (!url.startsWith(base) || url.startsWith(`${base}/api/v1/view-as/`)) return realFetch(input, init);
    if (!v) {
      // Ended: nothing goes out as the member any more.
      return Promise.resolve(new Response(JSON.stringify({ detail: 'view_as_ended' }),
        { status: 401, headers: { 'Content-Type': 'application/json' } }));
    }
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    headers.set(VIEW_AS_HEADER, v.token);
    return realFetch(input, { ...init, headers });
  };
  if (navigator.sendBeacon) {
    const realBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) =>
      String(url).startsWith(base) ? true : realBeacon(url, data);
  }
  // Ends on time even if the tab sits idle: the banner and the requests stop.
  const v = liveViewAs();
  if (v) {
    const ms = Date.parse(v.expires_at) - Date.now();
    if (ms > 0) window.setTimeout(() => markViewAsEnded(), Math.min(ms, 2 ** 31 - 1));
  }
}

export type ViewAsBoot = { error?: string };

export async function bootViewAs(): Promise<ViewAsBoot> {
  if (typeof window === 'undefined') return {};
  const m = window.location.hash.match(/(?:^#|&)view_as=([^&]+)/);
  if (m) {
    // Off the address bar before anything else can read or share it.
    try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch { /* ignore */ }
    try {
      const res = await fetch(`${apiService.getApiBaseUrl()}/api/v1/view-as/claim`, {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: decodeURIComponent(m[1]) }),
      });
      const data = await res.json().catch(() => ({} as any));
      if (!res.ok || !data?.token) {
        // 410 carries the server's own sentence (expired / already used).
        return { error: res.status === 410 && data?.detail ? String(data.detail)
          : 'This View as member link did not work. Start it again from the admin panel.' };
      }
      const v: ViewAs = {
        token: data.token, user_id: data.user_id, email: data.email || '', name: data.name || '',
        mode: data.mode === 'write' ? 'write' : 'read', expires_at: data.expires_at,
        admin_email: data.admin_email || '', session_id: data.session_id,
      };
      setViewAs(v);
    } catch {
      return { error: 'Could not reach the server to start View as member.' };
    }
  }
  if (isViewAsTab()) install();
  return {};
}
